// src/components/UserMeet/BreakoutTimerBanner.jsx
import React, { useEffect, useState } from "react";

function getRemainingSeconds(expiresAt) {
  if (!expiresAt) return 0;
  return Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000));
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function BreakoutTimerBanner({
  expiresAt,
  ownerDisconnected,
  gracePeriodSeconds,
  visible,
}) {
  const [remainingSeconds, setRemainingSeconds] = useState(
    getRemainingSeconds(expiresAt)
  );
  const [graceCountdown, setGraceCountdown] = useState(gracePeriodSeconds);

  // Main timer
  useEffect(() => {
    if (!expiresAt) return;
    const interval = setInterval(() => {
      setRemainingSeconds(getRemainingSeconds(expiresAt));
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  // Grace period countdown
  useEffect(() => {
    if (!ownerDisconnected) {
      setGraceCountdown(gracePeriodSeconds);
      return;
    }

    setGraceCountdown(gracePeriodSeconds);
    const interval = setInterval(() => {
      setGraceCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [ownerDisconnected, gracePeriodSeconds]);

  if (!visible) return null;

  const isWarning = remainingSeconds <= 120 && remainingSeconds > 0;
  const isDanger = remainingSeconds <= 30;

  return (
    <div
      style={{
        position: "fixed",
        top: 72,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9998,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        alignItems: "center",
        pointerEvents: "none",
      }}
    >
      {/* Main timer */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 18px",
          borderRadius: 99,
          background: isDanger
            ? "rgba(251,113,133,0.25)"
            : isWarning
            ? "rgba(252,211,77,0.2)"
            : "rgba(0,0,0,0.55)",
          border: "1px solid",
          borderColor: isDanger
            ? "rgba(251,113,133,0.5)"
            : isWarning
            ? "rgba(252,211,77,0.4)"
            : "rgba(255,255,255,0.12)",
          backdropFilter: "blur(10px)",
          color: isDanger ? "#FB7185" : isWarning ? "#FCD34D" : "#fff",
          fontWeight: 700,
          fontSize: 14,
        }}
      >
        <span>🔒</span>
        <span>Private Meeting</span>
        <span
          style={{
            padding: "2px 10px",
            borderRadius: 99,
            background: isDanger
              ? "rgba(251,113,133,0.2)"
              : isWarning
              ? "rgba(252,211,77,0.15)"
              : "rgba(255,255,255,0.1)",
            fontFamily: "monospace",
            fontSize: 15,
          }}
        >
          {formatTime(remainingSeconds)}
        </span>
      </div>

      {/* Owner disconnected banner */}
      {ownerDisconnected && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 18px",
            borderRadius: 99,
            background: "rgba(252,211,77,0.2)",
            border: "1px solid rgba(252,211,77,0.4)",
            backdropFilter: "blur(10px)",
            color: "#FCD34D",
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          ⚠️ Host disconnected — ends in{" "}
          <span style={{ fontWeight: 700, fontFamily: "monospace" }}>
            {graceCountdown}s
          </span>
        </div>
      )}
    </div>
  );
}